/**
 * The apparatus catalogue. Every tile here PUTS SOMETHING ON THE BENCH — none
 * of them alters what is already there. That split is the whole organising
 * idea: the panel is the cupboard you fetch equipment from, and changing a
 * piece of apparatus already out on the bench is a right-click on that piece
 * (ContainerContextMenu / StandContextMenu), which acts on the thing you
 * pointed at rather than on whichever one happens to be selected.
 *
 * A stand arrives carrying the glassware it holds, and a burner arrives as a
 * complete heating station, because neither is any use alone.
 *
 * Tools are the exception, and are not items: they are the things you dip INTO
 * the selected container. Emptying or removing a container is not a tool — it
 * acts on the vessel itself, so it lives on that vessel's right-click menu.
 * How HOT is not here either: that scale belongs to a particular flame, so it
 * sits on the bench beside it (BenchTemperature).
 */
import { useEffect, useRef, useState } from 'react';
import { VESSEL_TYPES, VESSEL_LABELS, type VesselType } from '../vesselTypes';
import { STAND_TYPES, STAND_HINTS, STAND_LABELS, STAND_HOME_VESSEL, type StandType } from '../standTypes';
import { BURNER_TYPES, BURNER_LABELS, BURNER_HINTS, type BurnerType } from '../burnerTypes';
import { SUBSTANCE_CATALOG, findSubstance, type Substance } from '../substanceCatalog';
import { useResponsive } from '../../responsive';
import { StandIcon, VesselGlyph, BurnerIcon, ToolIcon } from './ApparatusIcons';
import { SubstanceIcon } from './SubstanceIcon';
import { ChemFormula } from './ChemFormula';

interface ControlsPanelProps {
  onPlaceVessel: (type: VesselType) => void;
  /** Right-click: place the vessel on the selected container's own stand. */
  onPlaceVesselConnected: (type: VesselType) => void;
  /** Places the stand, carrying the glassware it is built for. */
  onPlaceStand: (stand: StandType) => void;
  hasGlassRod: boolean;
  glassRodSubstanceId: string | null;
  onDipGlassRod: (substanceId: string) => void;
  onRemoveGlassRod: () => void;
  hasPHPaper: boolean;
  onToggleTest: () => void;
  /** The burner under the selected container, or 'none'. */
  burnerType: BurnerType;
  /** Whether the selected container's stand can take a burner at all. */
  canHeat: boolean;
  /** Tapping the lit burner again takes it away — see BURNER_TYPES' own note. */
  onChangeBurner: (burner: BurnerType) => void;
  canAddContainer: boolean;
  hasSubstances: boolean;
  disabled: boolean;
  /** A guided experiment declares the glassware it starts on, so the cupboard
   *  has nothing to offer: the vessel and stand rows are hidden rather than
   *  left there to be refused one tile at a time. Burner and tools stay — a
   *  script can ask for either. */
  benchFixed?: boolean;
  /** Whether this is the pane showing, on a phone. The panel stays mounted
   *  behind the other tabs, so without this the rod's reagent list was still
   *  open on coming back — there without Glass Rod having been tapped. */
  paneOpen?: boolean;
}

/**
 * One sub-section of the apparatus panel: a title over a row of picker tiles,
 * which folds away.
 *
 * The panel is four groups deep and a bench only ever uses two of them at a
 * time — the stand in particular is chosen once and then never again, while it
 * goes on taking a third of the column for the whole session. So each group is
 * a disclosure, its own state remembered, and the ones a fresh bench does not
 * need start closed.
 *
 * The title is the control: a heading that folds a section is a heading you
 * can hit anywhere, and a separate chevron button beside it would be a second
 * target for the same thing.
 *
 * Not on a phone: there the groups are always open and the title is only a
 * title (see below).
 */
function ApparatusGroup({ title, children, note, startOpen = true, className }: {
  title: string; children?: React.ReactNode; note?: string; startOpen?: boolean; className?: string;
}): React.JSX.Element {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(`chemlab.group.${title}`);
      if (saved === 'open' || saved === 'shut') return saved === 'open';
    } catch { /* not stored */ }
    return startOpen;
  });
  const toggle = (): void => {
    setOpen(next => {
      try { localStorage.setItem(`chemlab.group.${title}`, next ? 'shut' : 'open'); } catch { /* not persisted */ }
      return !next;
    });
  };
  // On a phone every group is always out: four to a row, the whole cupboard
  // is a few short rows, and a fold there only hid a row behind a tap.
  const { isMobile } = useResponsive();
  const shown = open || isMobile;
  return (
    <div className={`chemlab-apparatus-group${className ? ` ${className}` : ''}`}>
      {isMobile ? (
        <h3 className="chemlab-section-title chemlab-group-toggle">{title}</h3>
      ) : (
        <button
          className="chemlab-section-title chemlab-group-toggle"
          aria-expanded={open}
          onClick={toggle}
        >
          <span className="chemlab-group-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
          {title}
        </button>
      )}
      {shown && children && <div className="chemlab-apparatus-row">{children}</div>}
      {shown && note && <p className="chemlab-empty-hint">{note}</p>}
    </div>
  );
}

function ApparatusTile({ active, disabled, title, onClick, onContextMenu, icon, label }: {
  active?: boolean; disabled?: boolean; title: string; onClick: () => void;
  onContextMenu?: () => void; icon: React.ReactNode; label: string;
}): React.JSX.Element {
  return (
    <button
      className={`chemlab-apparatus-tile${active ? ' active' : ''}`}
      disabled={disabled}
      title={title}
      onClick={onClick}
      onContextMenu={onContextMenu && ((e) => { e.preventDefault(); onContextMenu(); })}
    >
      {icon}
      <span className="chemlab-apparatus-label">{label}</span>
    </button>
  );
}

export function ControlsPanel({
  onPlaceVessel, onPlaceVesselConnected, onPlaceStand,
  hasGlassRod, glassRodSubstanceId, onDipGlassRod, onRemoveGlassRod,
  hasPHPaper, onToggleTest, canAddContainer, hasSubstances, disabled,
  burnerType, canHeat, onChangeBurner, benchFixed, paneOpen,
}: ControlsPanelProps): React.JSX.Element {
  const benchFull = !canAddContainer;
  const [dipMenuOpen, setDipMenuOpen] = useState(false);
  useEffect(() => { if (!paneOpen) setDipMenuOpen(false); }, [paneOpen]);
  // On a phone the list runs on below the fold of a short pane; scroll the
  // Tools row to the top so the tap visibly did something, with the rod it
  // came from still in sight above the list.
  const dipMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!dipMenuOpen) return;
    const group = dipMenuRef.current?.closest('.chemlab-apparatus-group');
    const pane = dipMenuRef.current?.closest('.chemlab-side-panel');
    if (group instanceof HTMLElement && pane instanceof HTMLElement && pane.scrollHeight > pane.clientHeight) {
      const top = pane.scrollTop + group.getBoundingClientRect().top - pane.getBoundingClientRect().top;
      pane.scrollTo({ top, behavior: 'smooth' });
    }
  }, [dipMenuOpen]);
  const rodSubstance = glassRodSubstanceId ? findSubstance(glassRodSubstanceId) : undefined;
  // A rod is dipped in a REAGENT, so only pourable ones are offered — you
  // cannot dip a rod in a gas, and a solid does not coat it.
  const dippable: Substance[] = SUBSTANCE_CATALOG.filter(
    s => s.physicalState === 'liquid' || s.physicalState === 'aqueous',
  );
  return (
    <div className="chemlab-apparatus-groups">
      {!benchFixed && (<>
      <ApparatusGroup
        title="Container"
        note={benchFull ? 'The bench is full. Remove a container to make room.' : undefined}
      >
        {VESSEL_TYPES.map(type => (
          <ApparatusTile
            key={type}
            disabled={benchFull}
            title={`${VESSEL_LABELS[type]} — tap to place, right-click to place connected`}
            onClick={() => onPlaceVessel(type)}
            onContextMenu={() => onPlaceVesselConnected(type)}
            icon={<VesselGlyph vessel={type} />}
            label={VESSEL_LABELS[type]}
          />
        ))}
      </ApparatusGroup>

      <ApparatusGroup
        title="Stand"
        className="chemlab-apparatus-group-stand"
        /* Closed on a fresh bench: a vessel comes with the stand it needs, and
           changing it is a deliberate act rather than a first move. */
        startOpen={false}
        note={benchFull ? 'The bench is full. Remove a container to make room.' : undefined}
      >
        {STAND_TYPES.map(stand => (
          <ApparatusTile
            key={stand}
            disabled={benchFull}
            title={`${STAND_LABELS[stand]} — ${STAND_HINTS[stand]} Arrives with a ${VESSEL_LABELS[STAND_HOME_VESSEL[stand]].toLowerCase()}.`}
            onClick={() => onPlaceStand(stand)}
            icon={<StandIcon stand={stand} />}
            label={STAND_LABELS[stand]}
          />
        ))}
      </ApparatusGroup>
      </>)}

      <ApparatusGroup
        title="Burner"
        className="chemlab-apparatus-group-burner"
        note={canHeat
          ? undefined
          : 'This container is not on a stand that takes a burner.'}
      >
        {BURNER_TYPES.map(burner => (
          <ApparatusTile
            key={burner}
            active={burnerType === burner}
            disabled={!canHeat}
            title={burnerType === burner
              ? `Take the ${BURNER_LABELS[burner].toLowerCase()} away.`
              : `${BURNER_LABELS[burner]} — ${BURNER_HINTS[burner]}`}
            // Tapping the lit one again removes it, which is why this is a
            // toggle rather than a picker with a separate "none".
            onClick={() => onChangeBurner(burnerType === burner ? 'none' : burner)}
            icon={<BurnerIcon burner={burner} />}
            label={BURNER_LABELS[burner]}
          />
        ))}
      </ApparatusGroup>

      <ApparatusGroup title="Tools" className="chemlab-apparatus-group-tools">
      <ApparatusTile
        active={hasPHPaper}
        disabled={disabled || !hasSubstances}
        title={hasPHPaper ? 'Take the pH paper out' : 'Dip pH paper in the selected container'}
        onClick={onToggleTest}
        icon={<ToolIcon tool="ph-paper" />}
        label={hasPHPaper ? 'Take pH Paper Out' : 'pH Paper'}
      />
      <ApparatusTile
        active={hasGlassRod || dipMenuOpen}
        disabled={disabled}
        title={hasGlassRod ? 'Take the glass rod out' : 'Dip a glass rod in a reagent'}
        onClick={() => {
          if (hasGlassRod) { onRemoveGlassRod(); setDipMenuOpen(false); return; }
          setDipMenuOpen(o => !o);
        }}
        icon={<ToolIcon tool="glass-rod" />}
        label={hasGlassRod ? 'Remove Glass Rod' : 'Glass Rod'}
      />
      {/* A rod carries a reagent — the classic test is conc. HCl on a rod held
          over ammonia — so which one is on it is the point, and the student
          says so up front rather than the rod silently picking something up. */}
      {dipMenuOpen && !hasGlassRod && (
        <div ref={dipMenuRef} className="chemlab-add-menu chemlab-dip-menu">
          {dippable.map(s => (
            <button
              key={s.id}
              className="chemlab-dip-item"
              title={s.formula ? `${s.displayName} (${s.formula})` : s.displayName}
              onClick={() => { onDipGlassRod(s.id); setDipMenuOpen(false); }}
            >
              <SubstanceIcon physicalState={s.physicalState} color={s.color} form={s.form} unit={s.dose?.unit} />
              <span className="chemlab-dip-label">
                <ChemFormula formula={s.formula} fallback={s.displayName} />
              </span>
            </button>
          ))}
        </div>
      )}
      {hasGlassRod && rodSubstance && (
        <p className="chemlab-empty-hint chemlab-rod-note" style={{ marginTop: 8 }}>
          The rod is wet with {rodSubstance.displayName}.
        </p>
      )}
      </ApparatusGroup>
    </div>
  );
}
